import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useRef } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import type { DraftPick, SquadDraft } from '@/features/fantasy/squadModel';
import { pitchLayout } from '@/features/fantasy/squadModel';
import type { FormationRules, Position } from '@scoring/rules.ts';

import { AppText } from '@/components/ui/AppText';
import { VerticalPitchMarkings } from '@/components/ui/VerticalPitchMarkings';
import {
  PITCH_VERTICAL_PADDING,
  ROW_GAP,
  ROW_MIN_HEIGHT,
} from '@/lib/pitch/formation';
import { EmptySlotChip, PlayerChip } from './PlayerChip';

const DROP_RADIUS = 56;

/** Top-to-bottom rows: attack at the top, keeper guarding the bottom goal. */
const ROW_ORDER: Position[] = ['FWD', 'MID', 'DEF', 'GK'];
const BENCH_ORDER: Position[] = ['GK', 'DEF', 'MID', 'FWD'];

interface TargetInfo {
  playerId: string;
  isStarting: boolean;
  position: Position;
  x: number;
  y: number;
}

export interface PitchViewProps {
  draft: SquadDraft;
  rules: FormationRules;
  /** edit: prices + drag/swap + empty slots. points: read-only with points. */
  mode: 'edit' | 'points';
  onSwap?: (playerIdA: string, playerIdB: string) => void;
  onChipPress?: (playerId: string) => void;
  onEmptySlotPress?: (position: Position) => void;
  /** Subtitle text per player in points mode (e.g. "12 pts"). */
  pointsFor?: (playerId: string) => string | undefined;
  /** false → render crests/flags instead of headshots (the photo/flag toggle). */
  showPhotos?: boolean;
  /** Named slot labels per position (e.g. LB·LCB·RCB·RB) shown on empty slots. */
  slotLabels?: Record<Position, string[]>;
}

/**
 * Horizontal mowing bands over the grass - alternating a touch lighter / darker
 * green for the classic top-down broadcast stripe pattern. Low-opacity so it
 * reads as mowing, never garish.
 */
function TurfStripes() {
  return (
    <View className="absolute inset-0" style={{ pointerEvents: 'none' }}>
      {Array.from({ length: 7 }).map((_, index) => (
        <View
          key={index}
          className="flex-1"
          style={{ backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
        />
      ))}
    </View>
  );
}

/**
 * The painted turf: mowing stripes, a floodlight depth gradient (lighter at top,
 * deeper at bottom), then the shared crisp WHITE markings — halfway line, centre
 * circle & spot, both penalty boxes, goal areas and penalty spots — drawn by the
 * self-measuring SVG {@link VerticalPitchMarkings}, identical to the match-lineup
 * pitch (attack at top, keeper at bottom).
 */
function PitchMarkings() {
  return (
    <View className="absolute inset-0" style={{ pointerEvents: 'none' }}>
      <TurfStripes />
      <LinearGradient
        colors={['rgba(255,255,255,0.07)', 'rgba(255,255,255,0)', 'rgba(0,0,0,0.12)']}
        locations={[0, 0.5, 1]}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        pointerEvents="none"
      />
      <VerticalPitchMarkings />
    </View>
  );
}

/**
 * The Floodlight pitch - the starting XI laid out by position rows over the
 * turf, the bench below. Every position row is ALWAYS drawn: filled chips first,
 * then dashed empty slots up to the formation count, so even an empty squad
 * reads as a real, consistent pitch. Drag a chip onto a same-position player on
 * the other side of the starting/bench divide to swap them.
 */
export function PitchView({
  draft,
  rules,
  mode,
  onSwap,
  onChipPress,
  onEmptySlotPress,
  pointsFor,
  showPhotos = true,
  slotLabels,
}: PitchViewProps) {
  const { t } = useTranslation();
  const nodes = useRef(new Map<string, View>());
  const targets = useRef(new Map<string, TargetInfo>());

  const pickInfo = useCallback(
    (playerId: string): DraftPick | undefined =>
      draft.picks.find((pick) => pick.player.id === playerId),
    [draft],
  );

  const registerLayout = useCallback((playerId: string, node: View | null) => {
    if (node) {
      nodes.current.set(playerId, node);
    } else {
      nodes.current.delete(playerId);
    }
  }, []);

  const remeasure = useCallback(() => {
    targets.current.clear();
    for (const [playerId, node] of nodes.current) {
      node.measureInWindow((x, y, width, height) => {
        const pick = draft.picks.find((candidate) => candidate.player.id === playerId);
        if (!pick) return;
        targets.current.set(playerId, {
          playerId,
          isStarting: pick.isStarting,
          position: pick.player.position,
          x: x + width / 2,
          y: y + height / 2,
        });
      });
    }
  }, [draft]);

  const handleDrop = useCallback(
    (playerId: string, absoluteX: number, absoluteY: number) => {
      const source = pickInfo(playerId);
      if (!source || !onSwap) return;
      let best: { id: string; distance: number } | null = null;
      for (const target of targets.current.values()) {
        if (target.playerId === playerId) continue;
        if (target.position !== source.player.position) continue;
        if (target.isStarting === source.isStarting) continue;
        const distance = Math.hypot(target.x - absoluteX, target.y - absoluteY);
        if (distance <= DROP_RADIUS && (!best || distance < best.distance)) {
          best = { id: target.playerId, distance };
        }
      }
      if (best) {
        onSwap(playerId, best.id);
      }
    },
    [onSwap, pickInfo],
  );

  const drag =
    mode === 'edit' && onSwap ? { onDrop: handleDrop, onDragStart: remeasure } : undefined;

  const layout = pitchLayout(draft, rules);
  const isEdit = mode === 'edit';

  const chipSubtitle = (pick: DraftPick): string | undefined =>
    mode === 'points' ? (pointsFor?.(pick.player.id) ?? '-') : undefined;

  const slotLabel = (position: Position, slotIndex: number): string =>
    slotLabels?.[position]?.[slotIndex] ?? t(`player.positionsShort.${position}`);

  const renderChip = (pick: DraftPick) => (
    <PlayerChip
      key={pick.player.id}
      pick={pick}
      subtitle={chipSubtitle(pick)}
      subtitleTone={mode === 'points' ? 'points' : 'price'}
      showPhotos={showPhotos}
      onPress={onChipPress}
      drag={drag}
      registerLayout={registerLayout}
    />
  );

  return (
    <View>
      {/* The turf */}
      <View className="mx-4 overflow-hidden rounded-card border border-pitch-line bg-pitch">
        <PitchMarkings />
        {/* Fixed position rows — never wrap, so the formation shape holds. Shared
            ROW_GAP / ROW_MIN_HEIGHT keep spacing identical to the match-lineup
            and Team-of-the-Round pitches. */}
        <View
          className="px-2"
          style={{ rowGap: ROW_GAP, paddingVertical: PITCH_VERTICAL_PADDING }}
        >
          {ROW_ORDER.map((position) => {
            const filled = layout.starters[position];
            const emptyCount = isEdit
              ? Math.max(0, layout.startingTarget[position] - filled.length)
              : 0;
            if (filled.length === 0 && emptyCount === 0) return null;
            return (
              <View
                key={position}
                className="flex-row items-center justify-evenly"
                style={{ minHeight: ROW_MIN_HEIGHT }}
              >
                {filled.map(renderChip)}
                {Array.from({ length: emptyCount }, (_, index) => (
                  <EmptySlotChip
                    key={`${position}-empty-${index}`}
                    positionLabel={slotLabel(position, filled.length + index)}
                    onPress={() => onEmptySlotPress?.(position)}
                  />
                ))}
              </View>
            );
          })}
        </View>
      </View>

      {/* The bench */}
      <View className="mx-4 mt-3 rounded-card border border-line bg-surface px-2 py-3">
        <AppText variant="micro" weight="bold" className="mb-2 ps-2 uppercase text-muted">
          {t('fantasy.builder.bench')}
        </AppText>
        <View className="flex-row flex-wrap justify-evenly" style={{ rowGap: 12 }}>
          {BENCH_ORDER.flatMap((position) => {
            const filled = layout.bench[position];
            const emptyCount = isEdit
              ? Math.max(0, layout.benchTarget[position] - filled.length)
              : 0;
            return [
              ...filled.map(renderChip),
              ...Array.from({ length: emptyCount }, (_, index) => (
                <EmptySlotChip
                  key={`bench-${position}-empty-${index}`}
                  positionLabel={t(`player.positionsShort.${position}`)}
                  onPress={() => onEmptySlotPress?.(position)}
                />
              )),
            ];
          })}
        </View>
      </View>
    </View>
  );
}
